import React, { useState } from 'react';
import { FadeIn, CTAButton, Section, SectionTitle, Accent, pad2 } from './Shared';
import { SecurePaymentNote } from './SecurePaymentNote';
import { useCountdown } from '../hooks/useCountdown';
import { markCheckoutStarted } from '../hooks/useExitIntent';
import { PRODUCTS } from './MetaPixel';
import { tomos, sessionFeatures, featureChips } from '../data/tomos';
import { bonuses } from '../data/bonuses';
import { Check, AlertTriangle, Plus, PrinterIcon, ArrowRightIcon, Lock, ZapIcon, ShieldCheck, BookOpenIcon, GiftIcon, CheckCircle2 } from 'lucide-react';
export const ProductTomos = () => {
  const [openTomo, setOpenTomo] = useState<number | null>(0);
  return <Section className="bg-slate-950" data-id="element-27" data-mp="2cpq91w5or8u9">
      <FadeIn data-id="element-28" data-mp="2coni5o8d56ec">
        <SectionTitle sub="Un tome tous les deux mois. Vous ouvrez, choisissez la séance et coachez." data-id="element-29" data-mp="2cof1rmd17iye">
          6 Tomes <Accent data-id="element-30" data-mp="30dg5iwax65xc">complets</Accent>
        </SectionTitle>
      </FadeIn>

      {/* Year timeline */}
      <FadeIn className="mx-auto mb-8 max-w-4xl" data-id="element-31" data-mp="358dnwtu39rmw">
        <div className="grid grid-cols-6 gap-1.5 md:gap-2" aria-label="Votre année, tome par tome" data-id="element-32" data-mp="2hktjwrmjgmnb">
          {tomos.map(t => <div key={t.number} data-id="element-33" data-mp="2cntac6qhd1rg">
              <div className={`h-1.5 rounded-full ${t.bar}`} data-id="element-34" data-mp="2ckfb37d458o8" />
              <p className={`mt-2 font-display text-[11px] font-extrabold uppercase md:text-sm ${t.text}`} data-id="element-35" data-mp="2rc6azh0ij8sa">
                Tome {t.number}
              </p>
              <p className="hidden text-xs text-slate-500 md:block" data-id="element-36" data-mp="30bt136nsks61">{t.months}</p>
            </div>)}
        </div>
      </FadeIn>

      <FadeIn className="mb-12 md:mb-16" data-id="element-37" data-mp="2wb972byim4o4">
        <ul className="mx-auto flex max-w-3xl flex-wrap justify-center gap-1.5 md:gap-2" data-id="element-38" data-mp="2wck8w68eh2cl">
          {featureChips.map(chip => <li key={chip} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-200 md:text-sm" data-id="element-39" data-mp="35b5z8nk45lnx">
              <Check size={12} className="text-sky-400" strokeWidth={3} aria-hidden="true" data-id="element-40" data-mp="1ax4p36j45c8j" />
              {chip}
            </li>)}
        </ul>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400 md:text-sm" data-id="element-41" data-mp="3b0fm7x0c9rpk">
          <PrinterIcon size={14} aria-hidden="true" data-id="element-42" data-mp="3fz0oe1z5ilgn" />
          <span data-id="element-43" data-mp="3k0c4ngw4xuc6">
            <span className="font-semibold text-slate-200" data-id="element-44" data-mp="3jzswut078mbv">Prêt à imprimer</span> ou à utiliser
            directement sur votre téléphone/tablette.
          </span>
        </p>
      </FadeIn>

      <h3 className="mb-5 text-center text-xs font-semibold uppercase tracking-widest text-slate-400" data-id="element-45" data-mp="3tyruzhnc8zkm">
        Ce que vous recevez
      </h3>
      <div className="mb-16 grid grid-cols-1 items-start gap-3 md:mb-24 md:grid-cols-2 md:gap-4 lg:grid-cols-3" data-id="element-46" data-mp="2q4cd9ghfqgr">
        {tomos.map((t, i) => {
        const isOpen = openTomo === i;
        return <FadeIn key={t.number} delay={Math.min(i * 0.04, 0.15)} data-id="element-47" data-mp="bjr50qpe8dlq" data-mpi={i}>
              <button type="button" onClick={() => setOpenTomo(isOpen ? null : i)} aria-expanded={isOpen} className={`group w-full rounded-2xl border border-t-[3px] bg-white/[0.03] p-5 text-left transition-[border-color,background-color] duration-200 ease-out hover:bg-white/[0.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 md:p-6 ${t.border} ${isOpen ? 'border-x-white/20 border-b-white/20' : 'border-x-white/10 border-b-white/10'}`} data-id="element-48" data-mp="gk53pi53ub73">
                <div className="flex items-start justify-between gap-3" data-id="element-49" data-mp="ho6tyzyl7r1l">
                  <div className="min-w-0" data-id="element-50" data-mp="1ed4pwli2pv0c">
                    <p className={`font-display text-xs font-extrabold uppercase tracking-wide ${t.text}`} data-id="element-51" data-mp="1eezatztchn4f">
                      Tome {t.number} · {t.months}
                    </p>
                    <h4 className="mt-1.5 font-display text-lg font-extrabold leading-tight text-white md:text-xl" data-id="element-52" data-mp="14liil08uwg2v">
                      {t.title}
                    </h4>
                    <p className="mt-1 text-sm text-slate-400" data-id="element-53" data-mp="1x4ebvu916c8t">{t.sessions}</p>
                  </div>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-white transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-54" data-mp="1sbl6r59nh1w7">
                    <Plus size={16} data-id="element-55" data-mp="1hvam8s37cvlj" />
                  </span>
                </div>
                <div className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-56" data-mp="13uzsnhaid2wo">
                  <div className="overflow-hidden" data-id="element-57" data-mp="1vkm5voa2hrb7">
                    <p className="pt-4 text-[15px] leading-relaxed text-slate-300" data-id="element-58" data-mp="1vlw5ihwkj7jy">{t.desc}</p>
                    <ul className="mt-3 space-y-2" data-id="element-59" data-mp="20jfkxl505qdh">
                      {t.details.map(d => <li key={d} className="flex items-start gap-2 text-sm leading-snug text-slate-300" data-id="element-60" data-mp="1mrrbjpm51g07">
                          <Check size={14} className={`mt-0.5 shrink-0 ${t.text}`} strokeWidth={3} aria-hidden="true" data-id="element-61" data-mp="1qt31jw8xc38x" />
                          {d}
                        </li>)}
                    </ul>
                  </div>
                </div>
              </button>
            </FadeIn>;
      })}
      </div>

      <FadeIn data-id="element-62" data-mp="7u54f9fcb1i0">
        <div className="grid gap-8 rounded-3xl border border-white/10 bg-slate-900/60 p-6 md:grid-cols-[0.9fr_1.1fr] md:gap-12 md:p-12" data-id="element-63" data-mp="7tn0dqmfrizj">
          <div className="flex flex-col" data-id="element-64" data-mp="hl9hg3lggca7">
            <h3 className="font-display text-3xl font-black uppercase italic leading-[1.05] tracking-tight text-white md:text-5xl" data-id="element-65" data-mp="hkzt0kg26d6m">
              Chaque séance <Accent data-id="element-66" data-mp="3l1zuv3jd9rlx">comprend</Accent>
            </h3>
          </div>
          <ul className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2" data-id="element-67" data-mp="3l4caha1njpvt">
            {sessionFeatures.map(f => <li key={f.title} className="flex items-start gap-3" data-id="element-68" data-mp="3g59hinyy5a47">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15" data-id="element-69" data-mp="3v175vk2mpa5v">
                  <Check size={12} className="text-emerald-400" strokeWidth={3} aria-hidden="true" data-id="element-70" data-mp="3q3fgawpgi04u" />
                </span>
                <div data-id="element-71" data-mp="15b3ual5ry0ug">
                  <p className="font-semibold leading-snug text-white" data-id="element-72" data-mp="15bn5lj0x5qzi">{f.title}</p>
                  <p className="text-sm leading-normal text-slate-400" data-id="element-73" data-mp="1cek5145gb0bo">{f.desc}</p>
                </div>
              </li>)}
          </ul>
        <p className="mt-5 text-lg font-semibold leading-snug text-sky-300 md:mt-auto md:text-xl" data-id="element-74" data-mp="1hcd7pn7jpc77">
              Vous ouvrez le TOME, choisissez la séance et coachez. Aussi simple que ça.
              365 cours haute performance. Prêts dès aujourd’hui.
            </p></div>
      </FadeIn>
    </Section>;
};
export const Bonuses = () => <Section className="bg-slate-900/40" data-id="element-75" data-mp="tlpow6w3d25v">
    <FadeIn data-id="element-76" data-mp="ymdovi2astc8">
      <SectionTitle sub="Des ressources exclusives — sans frais supplémentaires" data-id="element-77" data-mp="ym3vdki1o41s">
        + 5 Bonus <Accent data-id="element-78" data-mp="13lqay0lchwdr">stratégiques</Accent>
      </SectionTitle>
    </FadeIn>

    <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:mb-10 md:gap-4 lg:grid-cols-5" data-id="element-79" data-mp="18cq6p2iqa8eo">
      {bonuses.map((bono, i) => <FadeIn key={bono.num} delay={Math.min(i * 0.04, 0.15)} className={i === bonuses.length - 1 ? 'sm:col-span-2 lg:col-span-1' : ''} data-id="element-80" data-mp="axuf0y3lo8i1" data-mpi={i}>
          <article className="flex h-full gap-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-3 sm:flex-col sm:gap-0 sm:p-0" data-id="element-81" data-mp="fukwnpv1zc5k">
            <img src={bono.image} alt={`Couverture du bonus ${bono.num}\u00a0: ${bono.title}`} loading="lazy" decoding="async" width={1024} height={1024} className="aspect-square w-24 shrink-0 self-start rounded-xl object-cover sm:w-full sm:rounded-none" data-id="element-82" data-mp="2p4karnqgj8sb" />
            <div className="flex min-w-0 flex-1 flex-col sm:p-4" data-id="element-83" data-mp="2f6nxoqdym3rk">
              <p className="font-display text-xs font-extrabold uppercase tracking-wide text-sky-400" data-id="element-84" data-mp="2a8utu404plug">
                Bonus {bono.num}
              </p>
              <h3 className="mt-1 font-display text-base font-extrabold leading-tight text-white" data-id="element-85" data-mp="21fixnn6oj9gp">
                {bono.title}
              </h3>
              <p className="mt-1.5 text-sm leading-snug text-slate-400" data-id="element-86" data-mp="1whps8wd7ajhh">{bono.desc}</p>
              <div className="mt-auto flex items-center justify-between gap-2 pt-3" data-id="element-87" data-mp="1rhlcx9o13usu">
                <span className="text-sm text-slate-500 line-through" data-id="element-88" data-mp="1mhqkv2sioz67">{bono.value}</span>
                <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-400" data-id="element-89" data-mp="3bcpgz9yuvhwc">
                  Offert
                </span>
              </div>
            </div>
          </article>
        </FadeIn>)}
    </div>

    <FadeIn className="mx-auto flex max-w-3xl flex-col items-center justify-between gap-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] px-6 py-5 text-center md:flex-row md:text-left" data-id="element-90" data-mp="31gcm48rkqy00">
      <div className="flex items-center gap-3" data-id="element-91" data-mp="2rmcdvy0zxcn2">
        <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 md:flex" data-id="element-92" data-mp="2wffa6dx5r06y">
          <GiftIcon size={22} className="text-emerald-400" aria-hidden="true" data-id="element-93" data-mp="2ip22mco6hni5" />
        </span>
        <div data-id="element-94" data-mp="2nnn0i5fyjv3f">
          <p className="text-sm text-slate-300" data-id="element-95" data-mp="2nndc2mak9vzu">
            Valeur totale des 5 bonus&nbsp;: <span className="text-slate-500 line-through" data-id="element-96" data-mp="28qvz5qmq49b6">50&nbsp;€</span>
          </p>
          <p className="text-xs text-slate-500" data-id="element-97" data-mp="2dnmda8eywuty">Inclus dans votre pack, sans frais supplémentaires.</p>
        </div>
      </div>
      <p className="font-display text-3xl font-black uppercase italic leading-none tracking-tight text-emerald-400 md:text-4xl" data-id="element-98" data-mp="1u3dzj6o0q4vm">
        Offerts avec votre pack
      </p>
    </FadeIn>
  </Section>;
export const UrgencyCounter = () => {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown();
  const units = [{
    value: hours,
    label: 'h'
  }, {
    value: minutes,
    label: 'min'
  }, {
    value: seconds,
    label: 'sec'
  }];
  return <Section className="bg-slate-950 !py-14 md:!py-20" data-id="element-99" data-mp="290odcmo4vmui">
      <FadeIn className="mx-auto max-w-xl text-center" data-id="element-100" data-mp="28y25sn18t7ca">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-400" data-id="element-101" data-mp="2mqi08zg2vx1l">
          Prix de lancement — fin dans
        </p>
        <div className="mt-4 flex items-start justify-center gap-2 md:gap-3" role="timer" aria-live="off" data-id="element-102" data-mp="2ht8bzn4fdrfc">
          {units.map((unit, i) => <React.Fragment key={unit.label} data-id="element-103" data-mp="2rqwa8n18ph29" data-mpi={i}>
              {i > 0 && <span className="pt-3 font-display text-4xl font-black text-slate-600 md:pt-4 md:text-5xl" aria-hidden="true" data-id="element-104" data-mp="2rs7aicvh86om">
                  :
                </span>}
              <div className="flex w-20 flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] py-3 md:w-24 md:py-4" data-id="element-105" data-mp="2m6kbfm29gefh">
                <span className="font-display text-4xl font-black tabular-nums leading-none tracking-tight text-white md:text-5xl" data-id="element-106" data-mp="2h7z65885j4k6">
                  {pad2(unit.value)}
                </span>
                <span className="mt-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-500" data-id="element-107" data-mp="39vv3mf5jp0i0">
                  {unit.label}
                </span>
              </div>
            </React.Fragment>)}
        </div>

        <p className="mt-6 flex items-center justify-center gap-2 text-base font-semibold text-amber-300 md:text-lg" data-id="element-108" data-mp="30xjfcjs5iesq">
          <AlertTriangle size={18} aria-hidden="true" data-id="element-109" data-mp="1pg9x20krdst0" />
          Ensuite, le prix augmente. Pour de bon.
        </p>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left" data-id="element-110" data-mp="1kh7073exmsw4">
          <div className="mb-3 flex justify-between gap-3 text-sm text-slate-300" data-id="element-111" data-mp="79iehfmo68">
            <span className="flex items-center gap-2" data-id="element-112" data-mp="4y92br4nu1uw">
              <span className="h-2 w-2 animate-pulse rounded-full bg-sky-400" aria-hidden="true" data-id="element-113" data-mp="4yqyknrbog2t" />
              Places au prix de lancement
            </span>
            <span className="shrink-0 font-semibold text-sky-400" data-id="element-114" data-mp="dr2ma1v1xpf3">Plus que quelques-unes</span>
          </div>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={71} aria-valuemin={0} aria-valuemax={100} aria-label="Places déjà prises" data-id="element-115" data-mp="ioueyy3itfkc">
            <div className="h-full w-[71%] rounded-full bg-blue-500" data-id="element-116" data-mp="sntlaa0tcz46" />
          </div>
          <p className="text-right text-xs text-slate-500" data-id="element-117" data-mp="wq6sz3z4f73l">71&nbsp;% des places déjà prises</p>
        </div>
      </FadeIn>
    </Section>;
};

/** Shared offer card used by the pricing section and the final CTA. */
export const OfferCard = ({
  id,
  footnote,
  checklist
}: {
  id?: string;
  footnote: string;
  checklist?: string[];
}) => {
  const includes = [{
    icon: BookOpenIcon,
    label: '6 Tomes complets',
    detail: '365 séances prêtes à coacher',
    price: '69\u00a0€'
  }, {
    icon: GiftIcon,
    label: '5 Bonus exclusifs',
    detail: 'Adaptations, échauffements, retours au calme et scripts',
    price: '50\u00a0€'
  }];
  return <div id={id} className="relative rounded-3xl border border-sky-400/40 bg-slate-900 p-5 shadow-2xl shadow-blue-950/50 md:p-8" data-id="element-118" data-mp="akcufwsv2vmk">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-600 px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white" data-id="element-119" data-mp="jmcfr1awslvb">
        Le plus choisi
      </div>

      <p className="mt-2 text-center font-display text-xl font-black uppercase italic tracking-tight text-white md:text-2xl" data-id="element-120" data-mp="tbm6odu0nyhu">
        Fitness Fonctionnel <Accent data-id="element-121" data-mp="17c6md14mxe74">365™</Accent>
      </p>
      <p className="mt-1 text-center text-xs font-semibold uppercase tracking-widest text-slate-500" data-id="element-122" data-mp="12d3smcu3uueg">
        {checklist ? 'Ce qui est inclus' : 'Ce que comprend votre pack'}
      </p>

      {checklist ? <ul className="mt-5 space-y-2.5 border-y border-white/10 py-4" data-id="element-123" data-mp="1qxgo17xp8l4l">
          {checklist.map(item => <li key={item} className="flex items-start gap-2.5 text-sm text-slate-200 md:text-base" data-id="element-124" data-mp="1cy7j54ey1dfx">
              <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-emerald-400" aria-hidden="true" data-id="element-125" data-mp="1gz97urb8i73n" />
              {item}
            </li>)}
        </ul> : <ul className="mt-5 divide-y divide-white/10 border-y border-white/10" data-id="element-126" data-mp="136jlumvph26j">
          {includes.map(({
        icon: Icon,
        label,
        detail,
        price
      }) => <li key={label} className="flex items-center justify-between gap-3 py-3" data-id="element-127" data-mp="taq1wifnq6eq">
              <div className="flex min-w-0 items-start gap-3" data-id="element-128" data-mp="y8i4c6dxhax6">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5" data-id="element-129" data-mp="y6ouki80qaky">
                  <Icon size={16} className="text-sky-400" aria-hidden="true" data-id="element-130" data-mp="kbmiteghpr08" />
                </span>
                <div className="min-w-0" data-id="element-131" data-mp="h7no2itv972f">
                  <p className="text-sm font-semibold leading-snug text-white md:text-base" data-id="element-132" data-mp="h6m1bh3hmw4v">{label}</p>
                  <p className="text-xs leading-snug text-slate-400 md:text-sm" data-id="element-133" data-mp="c9vlmuvli2jz">{detail}</p>
                </div>
              </div>
              <span className="shrink-0 text-sm text-slate-500 line-through" data-id="element-134" data-mp="7frdw9ei9qhu">{price}</span>
            </li>)}
        </ul>}

      <div className="mt-6 text-center" data-id="element-135" data-mp="3umjypn2n34co">
        <p className="text-sm text-slate-400" data-id="element-136" data-mp="3unl9dqfqtotv">
          Valeur réelle <span className="text-slate-500 line-through" data-id="element-137" data-mp="3pngwsbhyhe8x">119&nbsp;€</span> · Accès complet aujourd’hui pour
        </p>
        <p className="mt-2 font-display font-black italic leading-none tracking-tight text-white" data-id="element-138" data-mp="3knugft6tzhvw">
          <span className="text-7xl md:text-8xl" data-id="element-139" data-mp="3gnamszlguwei">19</span>
          <span className="ml-1 align-top text-3xl md:text-4xl" data-id="element-140" data-mp="3govhcp7s6fcd">€</span>
        </p>
        <p className="mt-2 text-xs text-slate-400 md:text-sm" data-id="element-141" data-mp="1iw7y0g108vb1">{footnote}</p>
      </div>

      <SecurePaymentNote className="mt-5" data-id="element-142" data-mp="1evxgc63uoggc" />

      <div className="mt-5" data-id="element-143" data-mp="2wt26wwh4btyu">
        <CTAButton href={PRODUCTS.fitness365.checkoutUrl} fullWidth trackingProduct="fitness365" onClick={markCheckoutStarted} data-id="element-144" data-mp="2wtu3a9ijhvq6">
          Je veux mon accès
          <ArrowRightIcon size={18} className="transition-transform duration-200 group-hover/cta:translate-x-0.5" aria-hidden="true" data-id="element-145" data-mp="31psojo8kmxmd" />
        </CTAButton>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-500" data-id="element-146" data-mp="3aj5np7x8avgw">
          <span className="flex items-center gap-1" data-id="element-147" data-mp="2dvrb5lup3io5"><Lock size={12} aria-hidden="true" data-id="element-148" data-mp="2it9mtk03kbzu" /> Paiement sécurisé</span>
          <span className="flex items-center gap-1" data-id="element-149" data-mp="2ivdmfpqyqoub"><ZapIcon size={12} aria-hidden="true" data-id="element-150" data-mp="2mud1422hxzyy" /> Accès en quelques minutes</span>
          <span className="flex items-center gap-1" data-id="element-151" data-mp="2rty0gdlkwac7"><ShieldCheck size={12} aria-hidden="true" data-id="element-152" data-mp="2rv0sioun0itp" /> Garantie 7 jours</span>
        </div>
      </div>
    </div>;
};
export const Pricing = () => <Section className="bg-slate-900/40" id="pricing" data-id="element-153" data-mp="26qdudilgfd3">
    <div className="mx-auto max-w-lg" data-id="element-154" data-mp="72p6idaygjjb">
      <FadeIn className="mb-10 text-center" data-id="element-155" data-mp="75tezmvge1fs">
        <p className="text-sm text-slate-400 md:text-base" data-id="element-156" data-mp="2363njff774yi">
          Vous êtes à un pas de régler tous vos cours…
        </p>
        <h2 className="mx-auto mt-3 font-display text-[34px] font-black uppercase italic leading-[1.05] tracking-tight text-white md:text-6xl" data-id="element-157" data-mp="1z4qfqzsjoroy">
          Offre spéciale de <Accent data-id="element-158" data-mp="285g9zz25qa0k">lancement</Accent>
        </h2>
        <p className="mt-3 text-sm text-slate-400" data-id="element-159" data-mp="286ranq0b31ng">
          Tomes, bonus et 365 séances prêtes dans un seul accès.
        </p>
      </FadeIn>
      <FadeIn delay={0.05} data-id="element-160" data-mp="1fhkeuys8kq5w">
        <OfferCard footnote="Paiement unique · Sans abonnement · Accès immédiat" data-id="element-161" data-mp="1fibydb8k6xft" />
      </FadeIn>
    </div>
  </Section>;